import React, { useEffect, useState } from 'react'
import CurrencyConfiguration from './CurrencyConfiguration'
import WeightConfiguration from './WeightConfiguration'
import DistanceConfiguration from './DistanceConfiguration'
import { NUMBER_FORMATS, normalizeNumberDisplay, formatNumberValue } from '../../../common/numberFormat'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'richtext', label: 'Rich Text Field' },
  { value: 'number', label: 'Number' },
  { value: 'dateonly', label: 'Date Only' },
  { value: 'datetime', label: 'Date Time' },
  { value: 'time', label: 'Time' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'url', label: 'URL' },
]

export const displayFieldType = (type) => type === 'richtext' ? 'Rich Text Field' : type

export const createEmptyField = () => ({
  label: '',
  description: '',
  field_type: '',
  config: {},
  archived_at: null,
})

const verticalMenuProps = {
  MenuListProps: {
    sx: {
      '&&': {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
}

const verticalMenuItemSx = {
  '&&': {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
  },
}

const defaultConfigForType = (fieldType) => {
  switch (fieldType) {
    case 'time':
      return { clock_format: '24h' }
    case 'text':
      return { max: 500, multiline: false }
    case 'number':
      return { min: 0, max: 10000, scalefactor: 0 }
    case 'dropdown':
    case 'multiselect':
      return { options: [] }
    default:
      return {}
  }
}

const initializeField = (field) => {
  const source = field || createEmptyField()
  const cloned = JSON.parse(JSON.stringify(source))

  return {
    ...createEmptyField(),
    ...cloned,
    config: {
      ...defaultConfigForType(cloned.field_type),
      ...(cloned.config || {}),
    },
  }
}

const normalizeLabel = (label = '') => label.trim().toLowerCase()

const normalizeConfig = (fieldType, config = {}) => {
  if (fieldType === 'time') {
    return ['12h', '24h'].includes(config.clock_format) ? { config: { clock_format: config.clock_format } } : { error: 'Select a time format.' }
  }
  if (fieldType === 'text') {
    const max = Number(config.max)
    if (!Number.isInteger(max) || max < 0 || max > 1000) {
      return { error: 'Max Length must be a whole number between 0 and 1000.' }
    }
    return { config: { max, multiline: Boolean(config.multiline) } }
  }

  if (fieldType === 'number') {
    const percentage = config.format === 'percentage'
    const min = percentage ? 0 : Number(config.min)
    const max = percentage ? 100 : Number(config.max)
    const scalefactor = percentage ? 0 : Number(config.scalefactor)

    if (
      (!percentage && (config.min === '' || config.max === '' || config.scalefactor === '')) ||
      !Number.isInteger(min) ||
      !Number.isInteger(max) ||
      !Number.isInteger(scalefactor)
    ) {
      return { error: 'Min, Max, and Scale Factor must be whole numbers.' }
    }
    if (min > max) {
      return { error: 'Min cannot be greater than Max.' }
    }
    if (scalefactor < 0 || scalefactor > 100) {
      return { error: 'Scale Factor must be between 0 and 100.' }
    }

    const display = normalizeNumberDisplay(config)
    if (display.error) return display
    const normalized = { ...config, min, max, scalefactor, ...display.config }
    if (config.decimal_places === '') delete normalized.decimal_places
    if (config.format !== 'distance') delete normalized.distance_unit
    if (config.format !== 'weight') delete normalized.weight_unit
    if (config.format !== 'currency') {
      delete normalized.currency
      delete normalized.locale
    }
    return { config: normalized }
  }

  if (fieldType === 'dropdown' || fieldType === 'multiselect') {
    const options = config.options || []
    const incompleteOption = options.some(
      (option) =>
        Boolean(option.key?.trim()) !== Boolean(option.value?.trim())
    )

    if (incompleteOption) {
      return { error: 'Each option must have both a key and a value.' }
    }

    return {
      config: {
        options: options
          .filter((option) => option.key?.trim() && option.value?.trim())
          .map((option) => ({
            key: option.key.trim(),
            value: option.value.trim(),
          })),
      },
    }
  }

  return { config: {} }
}

const FieldEditDialog = ({
  open,
  field,
  existingFields = [],
  mode = 'edit',
  onClose,
  onSave,
}) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [editData, setEditData] = useState(() => initializeField(field))
  const [duplicateField, setDuplicateField] = useState(null)
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const isCreateMode = mode === 'create'

  useEffect(() => {
    if (open) {
      setEditData(initializeField(field))
      setTabIndex(0)
      setDuplicateField(null)
      setError('')
      setIsSaving(false)
    }
  }, [field, open, mode])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setEditData((previous) => ({
      ...previous,
      [name]: value,
      ...(name === 'field_type'
        ? { config: defaultConfigForType(value) }
        : {}),
    }))
    setError('')
  }

  const handleConfigChange = (event) => {
    const { name, value, type, checked } = event.target
    const newValue = type === 'checkbox' ? checked : value
    setEditData((previous) => ({
      ...previous,
      config: {
        ...previous.config,
        [name]: newValue,
      },
    }))
    setError('')
  }

  const addOption = () => {
    setEditData((previous) => ({
      ...previous,
      config: {
        ...previous.config,
        options: [
          ...(previous.config?.options || []),
          { key: '', value: '' },
        ],
      },
    }))
  }

  const updateOption = (index, fieldName, fieldValue) => {
    setEditData((previous) => {
      const options = [...(previous.config?.options || [])]
      options[index] = { ...options[index], [fieldName]: fieldValue }
      return {
        ...previous,
        config: {
          ...previous.config,
          options,
        },
      }
    })
    setError('')
  }

  const removeOption = (index) => {
    setEditData((previous) => {
      const options = [...(previous.config?.options || [])]
      options.splice(index, 1)
      return {
        ...previous,
        config: {
          ...previous.config,
          options,
        },
      }
    })
  }

  const prepareFieldForSave = () => {
    const label = editData.label?.trim() || ''
    const description = editData.description?.trim() || ''

    if (!label || !editData.field_type || (isCreateMode && !description)) {
      setTabIndex(0)
      setError(
        isCreateMode
          ? 'Please enter a field label, select a field type, and provide a description.'
          : 'Please enter a field label.'
      )
      return null
    }

    const normalizedConfig = normalizeConfig(
      editData.field_type,
      editData.config
    )
    if (normalizedConfig.error) {
      setTabIndex(1)
      setError(normalizedConfig.error)
      return null
    }

    return {
      ...editData,
      label,
      description,
      field_type: editData.field_type.toLowerCase(),
      config: normalizedConfig.config,
      archived_at: editData.archived_at ?? null,
    }
  }

  const submitField = async (preparedField) => {
    setIsSaving(true)
    setError('')
    try {
      await onSave(preparedField)
      onClose()
    } catch (saveError) {
      console.error('Error saving field:', saveError)
      setDuplicateField(null)
      setError(
        isCreateMode
          ? 'Failed to create field. Please try again.'
          : 'Failed to update field. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = async (skipDuplicateCheck = false) => {
    const preparedField = prepareFieldForSave()
    if (!preparedField) return

    const originalLabel = normalizeLabel(field?.label)
    const labelChanged =
      isCreateMode || normalizeLabel(preparedField.label) !== originalLabel
    const matchingField = labelChanged
      ? existingFields.find(
          (candidate) =>
            candidate.id !== preparedField.id &&
            normalizeLabel(candidate.label) ===
              normalizeLabel(preparedField.label)
        )
      : null

    if (!skipDuplicateCheck && matchingField) {
      setDuplicateField(matchingField)
      return
    }

    await submitField(preparedField)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      {duplicateField ? (
        <>
          <DialogTitle>Duplicate Field Name</DialogTitle>
          <DialogContent>
            <DialogContentText role='alert'>
              A field named &quot;{duplicateField.label}&quot; already exists.
              Using the same name for multiple fields can make it difficult to
              select the correct field in layouts. Do you want to continue
              anyway?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDuplicateField(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSave(true)}
              color='primary'
              disabled={isSaving}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            {isCreateMode ? 'Create New Field' : 'Edit Field'}
          </DialogTitle>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
            indicatorColor='primary'
            textColor='primary'
          >
            <Tab label='Basic Info' />
            <Tab label='Configuration' />
          </Tabs>

          <DialogContent>
            {error && (
              <Alert severity='error' style={{ marginBottom: 8 }}>
                {error}
              </Alert>
            )}

            {tabIndex === 0 && (
              <>
                <TextField
                  margin='dense'
                  label={isCreateMode ? 'Field Label' : 'Label'}
                  name='label'
                  fullWidth
                  value={editData.label || ''}
                  onChange={handleInputChange}
                />
                <TextField
                  margin='dense'
                  label='Description'
                  name='description'
                  fullWidth
                  value={editData.description || ''}
                  onChange={handleInputChange}
                />
                {isCreateMode ? (
                  <FormControl fullWidth margin='dense' variant='outlined'>
                    <InputLabel id='field-dialog-type-label'>
                      Field Type
                    </InputLabel>
                    <Select
                      labelId='field-dialog-type-label'
                      label='Field Type'
                      name='field_type'
                      value={editData.field_type || ''}
                      onChange={handleInputChange}
                      MenuProps={verticalMenuProps}
                      sx={{ '&& .MuiSelect-select': { textAlign: 'left' } }}
                    >
                      {FIELD_TYPES.map((fieldType) => (
                        <MenuItem
                          key={fieldType.value}
                          value={fieldType.value}
                          sx={verticalMenuItemSx}
                        >
                          {fieldType.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    margin='dense'
                    label='Field Type'
                    name='field_type'
                    fullWidth
                    value={displayFieldType(editData.field_type) || ''}
                    disabled
                  />
                )}
              </>
            )}

            {tabIndex === 1 && (
              <Box mt={2}>
                {editData.field_type === 'richtext' && (
                  <Alert severity='info'>
                    Rich text supports headlines, paragraphs, bold, italic, underline, links, and lists.
                    Each Rich Text Field occupies its own full-width row within a tab or executive card.
                    Articles allow up to 20,000 characters. Images and attachments are not supported.
                    Rich text is not available as a heatmap value. No additional configuration is needed.
                  </Alert>
                )}
                {editData.field_type === 'time' && (
                  <TextField select fullWidth label="Clock Format" name="clock_format" value={editData.config.clock_format || '24h'} onChange={handleConfigChange} helperText="Time only, to the minute. No date or timezone conversion.">
                    <MenuItem value="24h">24-hour (14:30)</MenuItem>
                    <MenuItem value="12h">12-hour (2:30 PM)</MenuItem>
                  </TextField>
                )}
                {editData.field_type === 'text' && (
                  <>
                    <TextField
                      margin='dense'
                      label='Max Length'
                      type='number'
                      fullWidth
                      name='max'
                      value={editData.config?.max ?? ''}
                      onChange={handleConfigChange}
                      inputProps={{ min: 0, max: 1000, step: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(editData.config?.multiline)}
                          onChange={handleConfigChange}
                          name='multiline'
                        />
                      }
                      label='Multiline'
                    />
                  </>
                )}

                {editData.field_type === 'number' && (
                  <>
                    <FormControl fullWidth margin='dense'>
                      <InputLabel id='number-format-label'>Number Format</InputLabel>
                      <Select
                        labelId='number-format-label'
                        label='Number Format'
                        name='format'
                        value={editData.config?.format ?? 'number'}
                        onChange={handleConfigChange}
                        MenuProps={verticalMenuProps}
                      >
                        {NUMBER_FORMATS.map(({ value, label }) => (
                          <MenuItem key={value} value={value} sx={verticalMenuItemSx}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {editData.config?.format === 'percentage' && (
                      <Box mt={1}>
                        Enter values from 0 to 100: 60 means 60%. Existing values are not rescaled.
                        <Box>Preview: <bdi>{formatNumberValue(60, { ...editData.config, decimal_places: editData.config.decimal_places === '' || editData.config.decimal_places === undefined ? 0 : Number(editData.config.decimal_places) })}</bdi></Box>
                      </Box>
                    )}
                    {editData.config?.format === 'distance' && (
                      <DistanceConfiguration config={editData.config} onChange={handleConfigChange} />
                    )}
                    {editData.config?.format === 'weight' && (
                      <WeightConfiguration config={editData.config} onChange={handleConfigChange} />
                    )}
                    {editData.config?.format === 'currency' && (
                      <CurrencyConfiguration config={editData.config} onChange={handleConfigChange} />
                    )}
                    <TextField
                      margin='dense'
                      label='Decimal Places'
                      name='decimal_places'
                      type='number'
                      fullWidth
                      value={editData.config?.decimal_places ?? ''}
                      onChange={handleConfigChange}
                      inputProps={{ min: 0, max: 20, step: 1 }}
                      helperText={editData.config?.format === 'percentage'
                        ? 'Maximum decimal places allowed for entry and shown in display. Leave blank for whole percentages.'
                        : editData.config?.format === 'currency'
                        ? 'Leave blank to use the standard decimal places for this currency.'
                        : 'Optional display precision. Leave blank to preserve the current display.'}
                    />
                    <TextField
                      margin='dense'
                      label='Min'
                      type='number'
                      fullWidth
                      name='min'
                      value={editData.config?.format === 'percentage' ? 0 : editData.config?.min ?? ''}
                      disabled={editData.config?.format === 'percentage'}
                      onChange={handleConfigChange}
                      inputProps={{ step: 1 }}
                    />
                    <TextField
                      margin='dense'
                      label='Max'
                      type='number'
                      fullWidth
                      name='max'
                      value={editData.config?.format === 'percentage' ? 100 : editData.config?.max ?? ''}
                      disabled={editData.config?.format === 'percentage'}
                      onChange={handleConfigChange}
                      inputProps={{ step: 1 }}
                    />
                    <TextField
                      margin='dense'
                      label='Scale Factor'
                      type='number'
                      fullWidth
                      name='scalefactor'
                      value={editData.config?.format === 'percentage' ? 0 : editData.config?.scalefactor ?? ''}
                      disabled={editData.config?.format === 'percentage'}
                      onChange={handleConfigChange}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                  </>
                )}

                {(editData.field_type === 'dropdown' ||
                  editData.field_type === 'multiselect') && (
                  <>
                    {(editData.config?.options || []).map((option, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', marginBottom: 8 }}
                      >
                        <TextField
                          margin='dense'
                          label='Key'
                          value={option.key}
                          onChange={(event) =>
                            updateOption(index, 'key', event.target.value)
                          }
                          style={{ marginRight: 8 }}
                        />
                        <TextField
                          margin='dense'
                          label='Value'
                          value={option.value}
                          onChange={(event) =>
                            updateOption(index, 'value', event.target.value)
                          }
                          style={{ marginRight: 8 }}
                        />
                        <Button
                          variant='outlined'
                          onClick={() => removeOption(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button variant='outlined' onClick={addOption}>
                      Add Option
                    </Button>
                  </>
                )}

                {!editData.field_type && (
                  <div>Select a field type to configure this field.</div>
                )}
                {editData.field_type &&
                  !['text', 'number', 'dropdown', 'multiselect'].includes(
                    editData.field_type
                  ) && <div>No additional configuration options.</div>}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} color='primary' disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSave()}
              color='primary'
              variant='contained'
              disabled={isSaving}
            >
              {isCreateMode ? 'Create Field' : 'Save'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default FieldEditDialog
